import type * as React from 'react';

import styled, { type StyledComponent } from '@emotion/styled';

import { InputSize, type HeightSize } from '~/constants/form';
import { colors, fontFamily } from '~/styles/constants';

interface ButtonProps {
  className?: string;
  disabled: boolean;
  hasOptionSelected: boolean;
  isMultiSelect?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick: (val: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref?: (node: any) => void;
  responsive?: boolean;
  size?: number;
  tabIndex?: number;
  type: string;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/ban-types
const SelectButtonStyled: StyledComponent<{}, ButtonProps, {}> = styled('button')<any>`
  background: transparent;
  border: none;
  font-family: ${fontFamily};
  ${(props) => (props.responsive ? 'width: 100%;' : `width: ${props.size}px;`)};
  text-align: left;
  font-size: 14px;
  color: ${(props) => (props.hasOptionSelected ? colors.gray600 : colors.placeholderColor)};
  position: relative;
  outline: none;
  text-decoration: underline;
  cursor: pointer;
  line-height: 19px;
  display: inline-block;

  :focus,
  &:active {
    border-color: ${colors.primaryAlternative};
  }

  :disabled {
    background-color: ${colors.form.greyDisabled};
    border-color: ${colors.form.greyDisabledBorder};
  }
`;

const StyledIcon = styled('i')`
  margin-left: 4px;
  font-size: 12px;
  color: ${colors.placeholderColor};
`;

interface SelectButtonProps {
  buttonRef?: (node: React.ReactNode) => void;
  children?: React.ReactNode;
  className?: string;
  dataAnalytics?: string;
  dataE2E?: string;
  dataPendo?: string;
  dataTestid?: string;
  disabled?: boolean;
  hasOptionSelected?: boolean;
  height?: HeightSize;
  invalid?: boolean;
  isMultiSelect?: boolean;
  onClick(evt: React.MouseEvent): void;
  responsive?: boolean;
  size?: InputSize;
  tabIndex?: number;
  text?: string;
}

const SelectButton = (props: SelectButtonProps) => {
  const {
    buttonRef,
    children,
    className,
    dataAnalytics,
    dataE2E,
    dataPendo,
    dataTestid,
    disabled,
    hasOptionSelected,
    isMultiSelect,
    onClick,
    responsive,
    size,
    tabIndex,
  } = props;

  const linkSize = size ? size : InputSize.medium;

  return (
    <SelectButtonStyled
      className={className}
      data-analytics={dataAnalytics}
      data-e2e={dataE2E}
      data-pendo={dataPendo}
      data-testid={dataTestid}
      disabled={!!disabled}
      hasOptionSelected={!!hasOptionSelected}
      isMultiSelect={isMultiSelect}
      onClick={onClick}
      ref={buttonRef}
      responsive={responsive}
      size={linkSize}
      tabIndex={tabIndex}
      type="button"
    >
      {children}
      <StyledIcon className="fa fa-chevron-down" />
    </SelectButtonStyled>
  );
};

export default SelectButton;
